import { useState, useEffect } from 'react';

// json import removed - return plain objects in React Router v7
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { useLoaderData, Link } from 'react-router';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Eye, Share2 } from 'lucide-react';
import { toast } from 'sonner';

import { Trans } from '@lingui/react/macro';

import Header from '~/components/Header';
import { prisma } from '~/utils/db.server';
import { createLogger } from '~/utils/logger.server';
import { sanitizeArticleContent, extractFirstImage } from '~/utils/babylovegrowth.server';
import { getCurrentUser } from '~/utils/db.server';
import { usePostHog, EVENTS } from '~/hooks/usePostHog';

const logger = createLogger('AcademyArticle');

// Define loader return type for proper type inference
interface ArticleData {
  id: string;
  slug: string;
  title: string;
  metaDescription: string | null;
  languageCode: string;
  publishedAt: string | null;
  createdAt: string;
  contentHtml: string;
}

interface RelatedArticle {
  id: string;
  slug: string;
  title: string;
  metaDescription: string | null;
  publishedAt: string | null;
  createdAt: string;
  image: string | null;
}

interface ArticleLoaderData {
  article: ArticleData;
  viewCount: number;
  relatedArticles: RelatedArticle[];
}

export async function loader(args: LoaderFunctionArgs) {
  const { params } = args;
  const slug = params.slug;

  if (!slug) {
    throw new Response('Not Found', { status: 404 });
  }

  const article = await prisma.article.findUnique({
    where: { slug },
  });

  if (!article || !article.published) {
    throw new Response('Not Found', { status: 404 });
  }

  // Track view
  const user = await getCurrentUser(args).catch(() => null);

  await prisma.$transaction([
    // Increment view count
    prisma.article.update({
      where: { slug },
      data: { viewCount: { increment: 1 } },
    }),
    // Record individual view
    prisma.articleView.create({
      data: {
        articleId: article.id,
        userId: user?.id || null,
      },
    }),
  ]);

  logger.info('Article viewed', {
    metadata: { slug, articleId: article.id, userId: user?.id, title: article.title },
  });

  // Fetch related articles (excluding current article)
  const relatedArticlesRaw = await prisma.article.findMany({
    where: {
      published: true,
      id: { not: article.id },
    },
    orderBy: { publishedAt: 'desc' },
    take: 4,
    select: {
      id: true,
      slug: true,
      title: true,
      metaDescription: true,
      publishedAt: true,
      createdAt: true,
      contentHtml: true,
    },
  });

  // Extract images for related articles
  const relatedArticles = relatedArticlesRaw.map((a) => ({
    id: a.id,
    slug: a.slug,
    title: a.title,
    metaDescription: a.metaDescription,
    publishedAt: a.publishedAt?.toISOString() || null,
    createdAt: a.createdAt.toISOString(),
    image: extractFirstImage(a.contentHtml || ''),
  }));

  // Sanitize HTML content before sending to client
  const sanitizedArticle = {
    ...article,
    contentHtml: sanitizeArticleContent(article.contentHtml),
  };

  return Response.json({
    article: sanitizedArticle,
    viewCount: article.viewCount + 1,
    relatedArticles,
  });
}

export const meta: MetaFunction = ({ data }) => {
  const loaderData = data as ArticleLoaderData | undefined;
  if (!loaderData?.article) {
    return [{ title: 'Article Not Found' }];
  }

  return [
    { title: `${loaderData.article.title} | Pavone Academy` },
    { name: 'description', content: loaderData.article.metaDescription || '' },
    { property: 'og:title', content: loaderData.article.title },
    { property: 'og:description', content: loaderData.article.metaDescription || '' },
    { property: 'og:type', content: 'article' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: loaderData.article.title },
    {
      name: 'twitter:description',
      content: loaderData.article.metaDescription || '',
    },
  ];
};

export default function AcademyArticle() {
  const { article, viewCount, relatedArticles } = useLoaderData<ArticleLoaderData>();
  const [isShared, setIsShared] = useState(false);
  const { track } = usePostHog();

  // Track article view on mount
  useEffect(() => {
    track(EVENTS.ARTICLE_VIEWED, {
      article_id: article.id,
      article_title: article.title,
      article_source: 'babylovegrowth',
      language: article.languageCode,
      view_count: viewCount,
    });
  }, [article.id]);

  const handleShare = async () => {
    const url = window.location.href;
    const title = article.title;
    const text = article.metaDescription || 'Check out this article:';

    try {
      if (navigator.share) {
        await navigator.share({
          title,
          text,
          url,
        });
        setIsShared(true);
        toast.success('Article shared!');
        setTimeout(() => setIsShared(false), 2000);

        // Track successful share
        track(EVENTS.ARTICLE_SHARED, {
          article_id: article.id,
          article_title: article.title,
          share_method: 'native',
        });
      } else {
        await navigator.clipboard.writeText(url);
        setIsShared(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setIsShared(false), 2000);

        // Track clipboard copy
        track(EVENTS.ARTICLE_SHARED, {
          article_id: article.id,
          article_title: article.title,
          share_method: 'clipboard',
        });
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(url);
          setIsShared(true);
          toast.success('Link copied to clipboard!');
          setTimeout(() => setIsShared(false), 2000);

          // Track fallback clipboard copy
          track(EVENTS.ARTICLE_SHARED, {
            article_id: article.id,
            article_title: article.title,
            share_method: 'clipboard_fallback',
          });
        } catch (clipboardError) {
          toast.error('Failed to share. Please copy the link manually.');
        }
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1 pt-24 pb-16">
        <article className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Article Header Row */}
          <div className="flex items-center justify-between mb-8">
            <Link
              to="/academy"
              className="inline-flex items-center gap-2 text-brand-dark-blue hover:text-brand-gold transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="font-medium">
                <Trans>Back to Academy</Trans>
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>
                    {new Date(
                      article.publishedAt || article.createdAt
                    ).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  <span>
                    {viewCount} <Trans>views</Trans>
                  </span>
                </div>
              </div>

              <button
                onClick={handleShare}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 hover:border-brand-dark-blue hover:bg-gray-50 transition-colors"
              >
                <Share2 className="h-4 w-4" />
                <span>
                  <Trans>Share</Trans>
                </span>
              </button>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >

            {/* Article Content */}
            <div
              className="academy-article"
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />

            {/* Footer CTA */}
            <div className="mt-12 p-6 bg-gradient-to-br from-brand-dark-blue/5 to-brand-gold/5 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                <Trans>Ready to practice?</Trans>
              </h3>
              <p className="text-gray-600 mb-4">
                <Trans>
                  Start improving your speaking skills with AI-powered feedback
                  and analysis.
                </Trans>
              </p>
              <Link
                to="/home"
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-brand-dark-blue text-white font-medium hover:bg-brand-dark-blue/90 transition-colors"
              >
                <Trans>Try Pavone Free</Trans>
              </Link>
            </div>
          </motion.div>
        </article>

        {/* Read More Section */}
        {relatedArticles.length > 0 && (
          <section className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              <Trans>Read More</Trans>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedArticles.map((relatedArticle) => (
                <Link
                  key={relatedArticle.id}
                  to={`/academy/${relatedArticle.slug}`}
                  className="group block"
                >
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300 h-full flex flex-col">
                    {/* Article Image */}
                    <div className="aspect-video bg-gradient-to-br from-brand-dark-blue/10 to-brand-gold/10 overflow-hidden">
                      <img
                        src={relatedArticle.image || '/images/academy/ai-generated.jpg'}
                        alt={relatedArticle.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>

                    {/* Article Content */}
                    <div className="p-4 flex-1 flex flex-col">
                      {/* Date */}
                      <span className="text-xs text-gray-500 mb-2">
                        {new Date(
                          relatedArticle.publishedAt || relatedArticle.createdAt
                        ).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>

                      {/* Title */}
                      <h3 className="text-sm font-semibold text-gray-900 mb-2 group-hover:text-brand-dark-blue transition-colors">
                        {relatedArticle.title}
                      </h3>

                      {/* Excerpt */}
                      <p className="text-xs text-gray-600 line-clamp-2 flex-1">
                        {relatedArticle.metaDescription}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t">
        <div className="max-w-[1080px] mx-auto px-4 flex flex-col items-center gap-3 md:flex-row md:justify-between">
          <p className="text-sm text-muted-foreground">
            © 2026 Pavone. <Trans>All rights reserved.</Trans>
          </p>
          <div className="flex items-center gap-6 text-sm">
            <a href="/privacy" className="hover:underline">
              <Trans>Privacy Policy</Trans>
            </a>
            <a href="/terms" className="hover:underline">
              <Trans>Terms of Service</Trans>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
